import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { supabase } from '../config/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on app start
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setUser({
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.name || session.user.email?.split('@')[0],
          avatar: session.user.user_metadata?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(session.user.user_metadata?.name || session.user.email?.split('@')[0])}&background=6366f1&color=fff`
        });
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setUser({
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.name || session.user.email?.split('@')[0],
          avatar: session.user.user_metadata?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(session.user.user_metadata?.name || session.user.email?.split('@')[0])}&background=6366f1&color=fff`
        });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Register function
  const register = async (userData) => {
    try {
      const { name, email, password, confirmPassword } = userData;

      // Validation
      if (!name || !email || !password || !confirmPassword) {
        toast.error('Please fill in all fields');
        return false;
      }

      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return false;
      }

      if (password.length < 6) {
        toast.error('Password must be at least 6 characters long');
        return false;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error('Please enter a valid email address');
        return false;
      }

      // Sign up with Supabase
      const { data, error } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password: password,
        options: {
          data: {
            name: name.trim(),
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name.trim())}&background=6366f1&color=fff`
          }
        }
      });

      if (error) {
        if (error.message.includes('already registered')) {
          toast.error('User with this email already exists');
        } else {
          toast.error(error.message || 'An error occurred during registration');
        }
        return false;
      }

      if (data.user) {
        // Check if email confirmation is required
        if (data.user.email_confirmed_at || data.session) {
          // User is confirmed and logged in
          setUser({
            id: data.user.id,
            email: data.user.email,
            name: name.trim(),
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name.trim())}&background=6366f1&color=fff`
          });
          toast.success('Account created successfully! Welcome to VELVETROOT!');
          return true;
        } else {
          // Email confirmation required
          toast.success('Account created! Please check your email to confirm your account before logging in.');
          return false; // Don't auto-login if email confirmation is required
        }
      }

      return false;
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('An error occurred during registration');
      return false;
    }
  };

  // Login function
  const login = async (credentials) => {
    try {
      const { email, password } = credentials;
      // Note: rememberMe is not used - Supabase handles session persistence automatically

      // Validation
      if (!email || !password) {
        toast.error('Please fill in all fields');
        return false;
      }

      // Sign in with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password: password,
      });

      if (error) {
        // Handle specific error cases
        if (error.message.includes('Email not confirmed') || error.message.includes('email_not_confirmed')) {
          toast.error('Please check your email and confirm your account before logging in. Or create a new account.');
        } else if (error.message.includes('Invalid login credentials') || error.message.includes('invalid_credentials')) {
          toast.error('Invalid email or password. Please check your credentials.');
        } else if (error.message.includes('User not found')) {
          toast.error('No account found with this email address.');
        } else {
          // Show the actual error message for debugging
          toast.error(error.message || 'Invalid email or password');
        }
        console.error('Login error details:', error);
        console.error('Error code:', error.status);
        return false;
      }

      if (data.user) {
        setUser({
          id: data.user.id,
          email: data.user.email,
          name: data.user.user_metadata?.name || data.user.email?.split('@')[0],
          avatar: data.user.user_metadata?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(data.user.user_metadata?.name || data.user.email?.split('@')[0])}&background=6366f1&color=fff`
        });
        toast.success(`Welcome back, ${data.user.user_metadata?.name || data.user.email?.split('@')[0]}!`);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Login error:', error);
      toast.error('An error occurred during login');
      return false;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        toast.error('Error logging out');
        return;
      }
      setUser(null);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('An error occurred during logout');
    }
  };

  // Update user profile
  const updateUser = async (updatedData) => {
    try {
      const { data: { user: currentUser }, error: fetchError } = await supabase.auth.getUser();
      
      if (fetchError || !currentUser) {
        toast.error('Failed to fetch user data');
        return false;
      }

      // Update user metadata
      const { data, error } = await supabase.auth.updateUser({
        data: {
          ...currentUser.user_metadata,
          ...updatedData
        }
      });

      if (error) {
        toast.error(error.message || 'Failed to update profile');
        return false;
      }

      if (data.user) {
        setUser({
          id: data.user.id,
          email: data.user.email,
          name: data.user.user_metadata?.name || data.user.email?.split('@')[0],
          avatar: data.user.user_metadata?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(data.user.user_metadata?.name || data.user.email?.split('@')[0])}&background=6366f1&color=fff`
        });
        toast.success('Profile updated successfully');
        return true;
      }

      return false;
    } catch (error) {
      console.error('Update user error:', error);
      toast.error('Failed to update profile');
      return false;
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return user !== null;
  };

  // Get user role (for future use)
  const getUserRole = () => {
    return user?.role || 'customer';
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    updateUser,
    isAuthenticated,
    getUserRole
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
